import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import {
  keepPreviousData,
  queryOptions,
  useQuery,
} from '@tanstack/react-query';
import { api } from 'lib/request/api';
import type { LegacyUnitEvaluationDownloadsResponse } from './types';

/**
 * The evaluation the list is filtered to, from the `evaluation_id` query param that old
 * links to the retired unit evaluation pages redirect with. Only a whole number counts.
 *
 * @return {string | null} The evaluation id, or null for the full list.
 */
export const useEvaluationIdFilter = (): string | null => {
  const { search } = useLocation();
  return useMemo(() => {
    const evaluationId = new URLSearchParams(search).get('evaluation_id');
    return evaluationId && /^\d+$/.test(evaluationId) ? evaluationId : null;
  }, [search]);
};

/**
 * One page of the legacy unit-evaluation downloads (read-only admin list).
 *
 * @param {number} page - 1-based page number.
 * @param {number} perPage - Rows per page.
 * @param {string | null} evaluationId - Limit to this evaluation's exports, or null for all.
 * @return {object} The React Query result for that page.
 */
export const useLegacyUnitEvaluationDownloads = (
  page: number,
  perPage: number,
  evaluationId: string | null
) =>
  useQuery(
    queryOptions({
      queryKey: [
        'admin',
        'legacy-unit-evaluation-downloads',
        page,
        perPage,
        evaluationId,
      ],
      queryFn: ({ signal }) =>
        api
          .get('admin/legacy_unit_evaluation_downloads', {
            searchParams: {
              page,
              per_page: perPage,
              ...(evaluationId ? { evaluation_id: evaluationId } : {}),
            },
            signal,
          })
          .json<LegacyUnitEvaluationDownloadsResponse>(),
      // Keep the current page on screen while the next one loads.
      placeholderData: keepPreviousData,
    })
  );
